import { Box, Button, CircularProgress } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { recordWidgetDataReady } from 'hooks/dashboard/dashboardWidgetTimings'

type GateMode = 'intersection' | 'expand' | 'after-idle'

/**
 * Does not mount children (and therefore does not start their Firestore queries)
 * until the section is near the viewport, the user expands it, the browser is idle
 * after primary content paints, or `enabled` is false.
 */
export const DeferredSection: React.FC<{
  children: React.ReactNode
  enabled?: boolean
  minHeight?: number
  timingId?: string
  /**
   * `intersection` — near viewport.
   * `expand` — explicit Load button (opt-in).
   * `after-idle` — auto-start after primary dashboard content has painted (no button).
   */
  mode?: GateMode
  expandLabel?: string
  /** Extra ms after idle before mounting (stagger heavy widgets without blocking peers). */
  idleDelayMs?: number
}> = ({
  children,
  enabled = true,
  minHeight = 120,
  timingId,
  mode = 'intersection',
  expandLabel = 'Load section',
  idleDelayMs = 0,
}) => {
  const [ready, setReady] = useState(!enabled)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!enabled || ready || mode !== 'intersection') return
    const node = sentinelRef.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      setReady(true)
      return
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setReady(true)
          observer.disconnect()
        }
      },
      { root: null, rootMargin: '160px 0px', threshold: 0.01 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [enabled, ready, mode])

  useEffect(() => {
    if (!enabled || ready || mode !== 'after-idle') return
    let cancelled = false
    let staggerId = 0
    const start = () => {
      if (cancelled) return
      if (idleDelayMs > 0) {
        staggerId = window.setTimeout(() => {
          if (!cancelled) setReady(true)
        }, idleDelayMs)
        return
      }
      setReady(true)
    }
    // Two rAFs so Financial Overview / above-fold widgets can paint first,
    // then idle (or a short timeout) before mounting heavy deferred queries.
    let raf2 = 0
    let idleId = 0
    let timeoutId = 0
    const w = window as Window & {
      requestIdleCallback?: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number
      cancelIdleCallback?: (id: number) => void
    }
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        if (typeof w.requestIdleCallback === 'function') {
          idleId = w.requestIdleCallback(start, { timeout: 1_200 })
        } else {
          timeoutId = window.setTimeout(start, 400)
        }
      })
    })
    return () => {
      cancelled = true
      window.cancelAnimationFrame(raf1)
      window.cancelAnimationFrame(raf2)
      w.cancelIdleCallback?.(idleId)
      if (timeoutId) window.clearTimeout(timeoutId)
      if (staggerId) window.clearTimeout(staggerId)
    }
  }, [enabled, ready, mode, idleDelayMs])

  useEffect(() => {
    if (!ready || !timingId) return
    const notes =
      mode === 'expand'
        ? 'explicit-expand'
        : mode === 'after-idle'
          ? 'after-idle'
          : 'intersection-observer'
    recordWidgetDataReady(`${timingId}:deferred-mount`, { notes })
  }, [ready, timingId, mode])

  if (!enabled) {
    return <>{children}</>
  }

  if (!ready) {
    return (
      <Box
        ref={sentinelRef}
        aria-hidden={mode !== 'expand'}
        sx={{
          minHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {mode === 'expand' ? (
          <Button variant='outlined' size='small' onClick={() => setReady(true)}>
            {expandLabel}
          </Button>
        ) : mode === 'after-idle' ? (
          <CircularProgress size={22} aria-label='Waiting to load section' />
        ) : null}
      </Box>
    )
  }

  return <>{children}</>
}
